// /master/ai-models — pagina master refactor card-based (Task #1463).
// Sostituisce la tabella legacy con cards collassabili per categoria, drawer
// override per-tenant, tab Audit dedicato. Mantiene "Testa Tutti" + "Aggiorna".
import { useState, useEffect, useCallback, useMemo } from 'react';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-master/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@easyflow/ui-master/components/ui/tabs';
import {
  Brain, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Activity,
  ArrowRightLeft, Shield, Clock, Cpu, Mic, FileText, Search as SearchIcon,
  ChevronDown, ChevronRight, PlayCircle, Loader2, Users, Trash2, History,
} from 'lucide-react';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { ModelCapabilityBadge } from '@easyflow/ui-ai-routing';
import type {
  AIRoutingServicesResponse,
  AIRoutingServiceItem,
  AIRoutingOverridesResponse,
  AIRoutingOverrideEntry,
} from '@easyflow/shared';

type VerifyStatusReason =
  | 'ok'
  | 'provider_down'
  | 'no_credentials'
  | 'provider_unresolved'
  | 'model_out_of_catalog'
  | 'model_deprecated'
  | 'no_model';

interface VerifyResult {
  currentModel: string;
  currentProvider: string;
  modelInCatalog: boolean;
  modelDeprecated: boolean;
  providerHealth: Record<string, { healthy: boolean; latencyMs: number; error?: string; reason?: string }>;
  currentModelStatus: 'online' | 'offline' | 'degraded';
  statusReason: VerifyStatusReason;
  suggestedModels: Array<{ model: string; provider: string; isUpgrade: boolean; isRecommended: boolean }>;
}

// Motivo dello stato in italiano, per messaggi UI chirurgici.
const STATUS_REASON_LABEL: Record<VerifyStatusReason, string> = {
  ok: 'Operativo',
  provider_down: 'Provider irraggiungibile',
  no_credentials: 'Nessuna credenziale configurata',
  provider_unresolved: 'Provider non risolvibile dal catalogo',
  model_out_of_catalog: 'Modello fuori catalogo',
  model_deprecated: 'Modello deprecato',
  no_model: 'Nessun modello assegnato',
};

function statusReasonLabel(r?: VerifyStatusReason): string | null {
  if (!r || r === 'ok') return null;
  return STATUS_REASON_LABEL[r] ?? null;
}

interface HistoryEntry {
  service_key: string;
  display_name: string;
  date: string;
  admin: string;
  old_model: string;
  new_model: string;
}

type ServiceStatus = 'online' | 'offline' | 'degraded';
type TestRunStatus = ServiceStatus | 'pending' | 'testing';

interface TestRunRow {
  serviceKey: string;
  displayName: string;
  status: TestRunStatus;
  latencyMs?: number;
  error?: string;
  reason?: VerifyStatusReason;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: typeof Brain; color: string }> = {
  embedding: { label: 'Embedding',          icon: SearchIcon, color: 'text-blue-500' },
  rag:       { label: 'RAG Pipeline',       icon: Cpu,        color: 'text-purple-500' },
  speech:    { label: 'Speech (TTS/STT)',   icon: Mic,        color: 'text-green-500' },
  content:   { label: 'Content Generation', icon: FileText,   color: 'text-orange-500' },
};
const CATEGORY_ORDER: ReadonlyArray<keyof typeof CATEGORY_CONFIG> = ['embedding', 'rag', 'speech', 'content'];

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toString();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function ProviderBadge({ provider }: { provider: string }) {
  const label = provider === 'openai' ? 'OpenAI'
    : provider === 'gemini' ? 'Gemini'
    : provider === 'anthropic' ? 'Claude'
    : provider === 'elevenlabs' ? 'ElevenLabs'
    : provider;
  return <Badge variant={provider === 'openai' ? 'default' : 'secondary'} className="text-xs">{label}</Badge>;
}

function StatusBadge({ status }: { status: ServiceStatus | 'default' }) {
  switch (status) {
    case 'online':   return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" /> Online</Badge>;
    case 'offline':  return <Badge variant="destructive" className="text-xs"><XCircle className="h-3 w-3 mr-1" /> Offline</Badge>;
    case 'degraded': return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs"><AlertTriangle className="h-3 w-3 mr-1" /> Degradato</Badge>;
    default:         return <Badge variant="outline" className="text-xs"><Activity className="h-3 w-3 mr-1" /> Non verificato</Badge>;
  }
}

interface ServiceCardProps {
  item: AIRoutingServiceItem;
  status: ServiceStatus | 'default';
  testing: boolean;
  onTest: () => void;
  onChangeModel: () => void;
  onShowOverrides: () => void;
}

function ServiceCard(props: ServiceCardProps) {
  const { item, status, testing, onTest, onChangeModel, onShowOverrides } = props;
  const [expanded, setExpanded] = useState(false);
  const overrideCount = item.overrides.count;

  return (
    <div className="rounded-xl border border-border/60 bg-background/40 hover:bg-muted/20 transition-colors">
      <div className="px-4 py-3 flex items-start gap-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 text-muted-foreground hover:text-foreground shrink-0"
          aria-label={expanded ? 'Comprimi' : 'Espandi'}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium truncate">{item.display_name}</span>
            <code className="text-[10px] text-muted-foreground font-mono">{item.service_key}</code>
            <StatusBadge status={status} />
            <ModelCapabilityBadge capabilities={item.capabilities} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <ProviderBadge provider={item.default.provider} />
            <code className="px-1.5 py-0.5 rounded bg-muted font-mono">{item.default.model}</code>
            <span className="opacity-60">·</span>
            <span>24h: <strong className="text-foreground">{formatNumber(item.usage.requests_24h)}</strong></span>
            <span>7gg: <strong className="text-foreground">{formatNumber(item.usage.requests_7d)}</strong></span>
            <span>tenant attivi: <strong className="text-foreground">{item.usage.active_tenants}</strong></span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {overrideCount > 0 && (
            <button
              onClick={onShowOverrides}
              className="px-2 py-1 rounded-md text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 flex items-center gap-1"
              title="Mostra override per-tenant"
            >
              <Users className="h-3 w-3" />
              {overrideCount} override
            </button>
          )}
          <Button variant="ghost" size="sm" onClick={onTest} disabled={testing} title="Verifica disponibilità">
            {testing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onChangeModel} title="Cambia modello">
            <ArrowRightLeft className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-3 pt-0 ml-7 text-xs text-muted-foreground space-y-1.5 border-t border-border/30">
          <div className="pt-3">
            <span className="font-medium text-foreground">Override per provider:</span>{' '}
            {Object.keys(item.overrides.byProvider).length === 0
              ? <span className="opacity-60">nessuno</span>
              : Object.entries(item.overrides.byProvider).map(([p, n]) => (
                  <span key={p} className="ml-1 px-1.5 py-0.5 rounded bg-muted text-foreground/80">{p}: {n}</span>
                ))
            }
          </div>
          <div>
            <span className="font-medium text-foreground">Capability:</span>{' '}
            {Object.entries(item.capabilities).filter(([, v]) => v).map(([k]) => k).join(', ') || <span className="opacity-60">—</span>}
          </div>
          <div>
            <span className="font-medium text-foreground">Categoria:</span> {item.category}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIModelsPlatform() {
  const { toast } = useToast();
  const [items, setItems] = useState<AIRoutingServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [verifyServiceKey, setVerifyServiceKey] = useState<string | null>(null);
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, ServiceStatus>>({});
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [changingModel, setChangingModel] = useState(false);
  const [selectedNewModel, setSelectedNewModel] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});

  const [testAllRunning, setTestAllRunning] = useState(false);
  const [testAllProgress, setTestAllProgress] = useState<{ current: number; total: number; results: TestRunRow[] }>({ current: 0, total: 0, results: [] });

  const [overridesOpen, setOverridesOpen] = useState(false);
  const [overridesServiceKey, setOverridesServiceKey] = useState<string | null>(null);
  const [overridesEntries, setOverridesEntries] = useState<AIRoutingOverrideEntry[]>([]);
  const [overridesLoading, setOverridesLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [servicesRes, historyRes] = await Promise.all([
        apiClient.get<AIRoutingServicesResponse>('/master/ai-routing/services'),
        apiClient.get<HistoryEntry[]>('/master/platform-models/history'),
      ]);
      if (servicesRes.success && servicesRes.data) setItems(servicesRes.data.items);
      if (historyRes.success && historyRes.data) setHistory(historyRes.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void loadData(); }, [loadData]);

  const handleVerify = useCallback(async (serviceKey: string) => {
    setVerifying(serviceKey);
    setVerifyResult(null);
    try {
      const res = await apiClient.post<VerifyResult>(`/master/platform-models/${serviceKey}/verify`);
      if (res.success && res.data) {
        setVerifyResult(res.data);
        setVerifyServiceKey(serviceKey);
        const status = res.data.currentModelStatus;
        if (status === 'online' || status === 'offline' || status === 'degraded') {
          setServiceStatuses((prev) => ({ ...prev, [serviceKey]: status }));
        }
      } else {
        toast({ title: 'Errore verifica', description: res.error || 'Errore sconosciuto', variant: 'destructive' });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore', description: message, variant: 'destructive' });
    } finally {
      setVerifying(null);
    }
  }, [toast]);

  const openChangeDialog = useCallback((serviceKey: string) => {
    setVerifyServiceKey(serviceKey);
    setChangeDialogOpen(true);
    setSelectedNewModel(null);
    if (!verifyResult || verifyServiceKey !== serviceKey) {
      void handleVerify(serviceKey);
    }
  }, [verifyResult, verifyServiceKey, handleVerify]);

  const handleChangeModel = useCallback(async () => {
    if (!verifyServiceKey || !selectedNewModel) return;
    setChangingModel(true);
    try {
      const res = await apiClient.put(`/master/platform-models/${verifyServiceKey}`, { model: selectedNewModel });
      if (res.success) {
        toast({ title: 'Modello aggiornato', description: `${verifyServiceKey} ora usa ${selectedNewModel}` });
        setChangeDialogOpen(false);
        setVerifyResult(null);
        void loadData();
      } else {
        toast({ title: 'Errore', description: res.error || 'Errore nel cambio modello', variant: 'destructive' });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore', description: message, variant: 'destructive' });
    } finally {
      setChangingModel(false);
    }
  }, [verifyServiceKey, selectedNewModel, toast, loadData]);

  const handleTestAll = useCallback(async () => {
    if (testAllRunning || items.length === 0) return;
    setTestAllRunning(true);
    const initial: TestRunRow[] = items.map((m) => ({
      serviceKey: m.service_key, displayName: m.display_name, status: 'pending',
    }));
    setTestAllProgress({ current: 0, total: items.length, results: initial });

    const updated = [...initial];
    for (let i = 0; i < items.length; i++) {
      const m = items[i];
      const baseRow = updated[i];
      if (!m || !baseRow) continue;
      updated[i] = { ...baseRow, status: 'testing' };
      setTestAllProgress({ current: i, total: items.length, results: [...updated] });
      try {
        const res = await apiClient.post<VerifyResult>(`/master/platform-models/${m.service_key}/verify`);
        const row = updated[i] ?? baseRow;
        if (res.success && res.data) {
          const st = res.data.currentModelStatus;
          const latency = Math.max(0, ...Object.values(res.data.providerHealth).map((h) => h.latencyMs || 0));
          updated[i] = { ...row, status: st, latencyMs: latency, reason: res.data.statusReason };
          if (st === 'online' || st === 'offline' || st === 'degraded') {
            setServiceStatuses((prev) => ({ ...prev, [m.service_key]: st }));
          }
        } else {
          updated[i] = { ...row, status: 'offline', error: res.error || 'Errore' };
        }
      } catch (err: unknown) {
        const row = updated[i] ?? baseRow;
        const message = err instanceof Error ? err.message : String(err);
        updated[i] = { ...row, status: 'offline', error: message };
      }
      setTestAllProgress({ current: i + 1, total: items.length, results: [...updated] });
    }
    setTestAllRunning(false);
    const passed = updated.filter((r) => r.status === 'online').length;
    const failed = updated.filter((r) => r.status === 'offline').length;
    const degraded = updated.filter((r) => r.status === 'degraded').length;
    toast({
      title: 'Test completato',
      description: `${passed} online, ${degraded} degradati, ${failed} offline su ${items.length} servizi`,
      variant: failed > 0 ? 'destructive' : 'default',
    });
  }, [items, testAllRunning, toast]);

  const openOverridesDrawer = useCallback(async (serviceKey: string) => {
    setOverridesServiceKey(serviceKey);
    setOverridesOpen(true);
    setOverridesLoading(true);
    setOverridesEntries([]);
    try {
      const res = await apiClient.get<AIRoutingOverridesResponse>(`/master/ai-routing/services/${serviceKey}/overrides`);
      if (res.success && res.data) setOverridesEntries(res.data.entries);
      else toast({ title: 'Errore', description: res.error || 'Caricamento override fallito', variant: 'destructive' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore', description: message, variant: 'destructive' });
    } finally {
      setOverridesLoading(false);
    }
  }, [toast]);

  const handleResetOverride = useCallback(async (tenantId: string) => {
    if (!overridesServiceKey) return;
    try {
      const res = await apiClient.delete(`/master/ai-routing/services/${overridesServiceKey}/overrides/${tenantId}`);
      if (res.success) {
        toast({ title: 'Override rimosso', description: `Tenant tornato al default platform` });
        setOverridesEntries((prev) => prev.filter((e) => e.tenant_id !== tenantId));
        void loadData();
      } else {
        toast({ title: 'Errore', description: res.error || 'Reset fallito', variant: 'destructive' });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore', description: message, variant: 'destructive' });
    }
  }, [overridesServiceKey, toast, loadData]);

  // KPI ridotti a 3 (Task #1463): degradati 24h, override attivi totali, modifiche 7gg.
  const kpi = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const degraded24h = items.filter((m) => {
      const st = serviceStatuses[m.service_key];
      return st === 'degraded' || st === 'offline';
    }).length;
    const overridesTotal = items.reduce((s, m) => s + m.overrides.count, 0);
    const changes7d = history.filter((h) => new Date(h.date).getTime() >= sevenDaysAgo).length;
    return { degraded24h, overridesTotal, changes7d };
  }, [items, serviceStatuses, history]);

  const overridesService = items.find((i) => i.service_key === overridesServiceKey);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-7 w-7 text-purple-600" />
            Modelli AI Piattaforma
          </h1>
          <p className="text-muted-foreground mt-1">Configurazione modelli AI cross-tenant per servizi interni</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="default" onClick={handleTestAll} disabled={testAllRunning || loading || items.length === 0} className="bg-green-600 hover:bg-green-700">
            {testAllRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
            {testAllRunning ? `Test ${testAllProgress.current}/${testAllProgress.total}` : 'Testa Tutti'}
          </Button>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Test Run banner */}
      {(testAllRunning || testAllProgress.results.length > 0) && (
        <GlassCard>
          <GlassCardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {testAllRunning ? <Loader2 className="h-4 w-4 animate-spin text-green-600" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}
                <span className="font-semibold text-sm">
                  {testAllRunning ? `Test in corso… ${testAllProgress.current}/${testAllProgress.total}` : `Test completato — ${testAllProgress.current}/${testAllProgress.total}`}
                </span>
              </div>
              {!testAllRunning && testAllProgress.results.length > 0 && (
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1 text-green-600"><CheckCircle2 className="h-3 w-3" />{testAllProgress.results.filter((r) => r.status === 'online').length} online</span>
                  <span className="flex items-center gap-1 text-yellow-600"><AlertTriangle className="h-3 w-3" />{testAllProgress.results.filter((r) => r.status === 'degraded').length} degradati</span>
                  <span className="flex items-center gap-1 text-red-600"><XCircle className="h-3 w-3" />{testAllProgress.results.filter((r) => r.status === 'offline').length} offline</span>
                  <button onClick={() => setTestAllProgress({ current: 0, total: 0, results: [] })} className="text-muted-foreground/70 hover:text-muted-foreground ml-2">✕</button>
                </div>
              )}
            </div>
            {testAllRunning && (
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${testAllProgress.total > 0 ? (testAllProgress.current / testAllProgress.total) * 100 : 0}%` }} />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1.5 max-h-[200px] overflow-y-auto">
              {testAllProgress.results.map((r) => (
                <div key={r.serviceKey} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${
                  r.status === 'online' ? 'bg-green-50 dark:bg-green-900/20' :
                  r.status === 'offline' ? 'bg-red-50 dark:bg-red-900/20' :
                  r.status === 'degraded' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                  r.status === 'testing' ? 'bg-blue-50 dark:bg-blue-900/20' :
                  'bg-muted/50 dark:bg-gray-800/50'
                }`}>
                  {r.status === 'testing' ? <Loader2 className="h-3 w-3 animate-spin text-blue-500 shrink-0" /> :
                   r.status === 'online'  ? <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" /> :
                   r.status === 'offline' ? <XCircle className="h-3 w-3 text-red-500 shrink-0" /> :
                   r.status === 'degraded'? <AlertTriangle className="h-3 w-3 text-yellow-500 shrink-0" /> :
                                            <Clock className="h-3 w-3 text-muted-foreground/70 shrink-0" />}
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{r.displayName}</span>
                    {statusReasonLabel(r.reason) && (
                      <span className="truncate text-[10px] text-muted-foreground/70">{statusReasonLabel(r.reason)}</span>
                    )}
                  </div>
                  {r.latencyMs !== undefined && <span className="text-muted-foreground/70 ml-auto shrink-0">{r.latencyMs}ms</span>}
                </div>
              ))}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* KPI ridotti a 3 */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
        <GlassCard>
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Degradati / Offline</p>
                <p className="text-2xl font-bold">{kpi.degraded24h}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">dal test corrente</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </GlassCardContent>
        </GlassCard>
        <GlassCard>
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Override attivi</p>
                <p className="text-2xl font-bold">{kpi.overridesTotal}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">tenant_service_routing</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </GlassCardContent>
        </GlassCard>
        <GlassCard>
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Modifiche 7gg</p>
                <p className="text-2xl font-bold">{kpi.changes7d}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">cambio modello platform</p>
              </div>
              <History className="h-8 w-8 text-purple-500" />
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Tabs Servizi | Audit */}
      <Tabs defaultValue="servizi">
        <TabsList>
          <TabsTrigger value="servizi">Servizi</TabsTrigger>
          <TabsTrigger value="audit">Audit ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="servizi" className="space-y-4 mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nessun servizio configurato</p>
          ) : (
            CATEGORY_ORDER.map((cat) => {
              const catItems = items.filter((m) => m.category === cat);
              if (catItems.length === 0) return null;
              const cfg = CATEGORY_CONFIG[cat];
              const CatIcon = cfg?.icon ?? Brain;
              const collapsed = collapsedCategories[cat] === true;
              return (
                <GlassCard key={cat}>
                  <GlassCardHeader>
                    <button
                      onClick={() => setCollapsedCategories((p) => ({ ...p, [cat]: !collapsed }))}
                      className="w-full flex items-center justify-between"
                    >
                      <GlassCardTitle className="flex items-center gap-2">
                        <CatIcon className={`h-5 w-5 ${cfg?.color ?? 'text-muted-foreground'}`} />
                        {cfg?.label ?? cat}
                        <Badge variant="outline" className="ml-2">{catItems.length} servizi</Badge>
                      </GlassCardTitle>
                      {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </GlassCardHeader>
                  {!collapsed && (
                    <GlassCardContent>
                      <div className="grid gap-2.5">
                        {catItems.map((it) => (
                          <ServiceCard
                            key={it.service_key}
                            item={it}
                            status={serviceStatuses[it.service_key] ?? 'default'}
                            testing={verifying === it.service_key}
                            onTest={() => handleVerify(it.service_key)}
                            onChangeModel={() => openChangeDialog(it.service_key)}
                            onShowOverrides={() => openOverridesDrawer(it.service_key)}
                          />
                        ))}
                      </div>
                    </GlassCardContent>
                  )}
                </GlassCard>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                Storico Modifiche
                <Badge variant="outline">{history.length}</Badge>
              </GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              {history.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nessuna modifica registrata</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Data</th>
                        <th className="pb-3 font-medium">Servizio</th>
                        <th className="pb-3 font-medium">Modello Precedente</th>
                        <th className="pb-3 font-medium">Nuovo Modello</th>
                        <th className="pb-3 font-medium">Admin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(0, 100).map((entry, idx) => (
                        <tr key={`${entry.service_key}-${entry.date}-${idx}`} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="py-2 text-xs text-muted-foreground">{formatDate(entry.date)}</td>
                          <td className="py-2"><span className="font-medium">{entry.display_name}</span></td>
                          <td className="py-2"><code className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-xs font-mono text-red-700 dark:text-red-400">{entry.old_model}</code></td>
                          <td className="py-2"><code className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-xs font-mono text-green-700 dark:text-green-400">{entry.new_model}</code></td>
                          <td className="py-2 text-xs text-muted-foreground">{entry.admin?.substring(0, 8) ?? '—'}…</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        </TabsContent>
      </Tabs>

      {/* Drawer Override per-tenant */}
      <Dialog open={overridesOpen} onOpenChange={setOverridesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Override per-tenant — {overridesService?.display_name ?? overridesServiceKey}
            </DialogTitle>
          </DialogHeader>
          {overridesLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
              <span className="text-muted-foreground">Caricamento override…</span>
            </div>
          ) : overridesEntries.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nessun tenant con override per questo servizio</p>
          ) : (
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium">Tenant</th>
                    <th className="pb-3 font-medium">Provider</th>
                    <th className="pb-3 font-medium">Modello</th>
                    <th className="pb-3 font-medium">Source</th>
                    <th className="pb-3 font-medium">Aggiornato</th>
                    <th className="pb-3 font-medium text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {overridesEntries.map((e) => (
                    <tr key={e.tenant_id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2">
                        <div className="font-medium">{e.tenant_name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{e.tenant_id.substring(0, 8)}…</div>
                      </td>
                      <td className="py-2"><ProviderBadge provider={e.provider} /></td>
                      <td className="py-2">
                        {e.model
                          ? <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">{e.model}</code>
                          : <span className="text-xs text-muted-foreground italic">default platform</span>
                        }
                      </td>
                      <td className="py-2 text-xs text-muted-foreground">{e.source}</td>
                      <td className="py-2 text-xs text-muted-foreground">{formatDate(e.updated_at)}</td>
                      <td className="py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleResetOverride(e.tenant_id)} title="Reset al default">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Cambia Modello (mantenuto, alimentato da verify) */}
      <Dialog open={changeDialogOpen} onOpenChange={setChangeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" />
              Cambia Modello — {items.find((m) => m.service_key === verifyServiceKey)?.display_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {verifying ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
                <span className="text-muted-foreground">Verifica modelli disponibili…</span>
              </div>
            ) : verifyResult ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(verifyResult.providerHealth).map(([prov, h]) => (
                    <div key={prov} className="p-3 rounded-lg border text-center">
                      <p className="text-xs text-muted-foreground mb-1 capitalize">{prov}</p>
                      {h.healthy ? <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" /> : <XCircle className="h-5 w-5 text-red-500 mx-auto" />}
                      {!h.healthy && h.error && <p className="text-[10px] text-muted-foreground mt-1 truncate" title={h.error}>{h.error}</p>}
                    </div>
                  ))}
                </div>
                {statusReasonLabel(verifyResult.statusReason) && (
                  <p className={`text-xs ${verifyResult.currentModelStatus === 'offline' ? 'text-red-500' : 'text-yellow-600'}`}>
                    {statusReasonLabel(verifyResult.statusReason)}
                  </p>
                )}
                <div>
                  <p className="text-sm font-medium mb-2">Modello attuale:</p>
                  <code className="px-2 py-1 rounded bg-muted text-sm font-mono">
                    {items.find((m) => m.service_key === verifyServiceKey)?.default.model}
                  </code>
                </div>
                {verifyResult.suggestedModels.length > 0 ? (
                  <div>
                    <p className="text-sm font-medium mb-2">Seleziona nuovo modello:</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {verifyResult.suggestedModels.map((s) => (
                        <button
                          key={s.model}
                          onClick={() => setSelectedNewModel(s.model)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left ${
                            selectedNewModel === s.model
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <div>
                            <code className="text-sm font-mono">{s.model}</code>
                            <p className="text-xs text-muted-foreground mt-0.5 capitalize">{s.provider}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {s.isRecommended && <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs">Consigliato</Badge>}
                            {s.isUpgrade && !s.isRecommended && <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-xs">Upgrade</Badge>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Nessun modello alternativo disponibile</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Errore nel caricamento dei modelli disponibili</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => verifyServiceKey && handleVerify(verifyServiceKey)}>Riprova</Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleChangeModel} disabled={!selectedNewModel || changingModel}>
              {changingModel ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Conferma Cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
